/**
 * Installation status check utility for middleware
 * Used to determine if the system has been installed
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export type InstallStatus = 'installed' | 'not_installed' | 'unreachable';

// Simple in-memory cache with TTL to avoid repeated API calls
interface CacheEntry {
  status: InstallStatus;
  timestamp: number;
}

let installStatusCache: CacheEntry | null = null;
const CACHE_TTL = 60 * 1000; // 1 minute cache

/**
 * Check if the system has been installed
 * Calls GET /install/status API endpoint
 *
 * @returns Promise<InstallStatus> - 'installed' if installed, 'not_installed' if not, 'unreachable' if API is down
 */
export async function checkInstallStatus(): Promise<InstallStatus> {
  // Check cache first
  if (installStatusCache) {
    const now = Date.now();
    if (now - installStatusCache.timestamp < CACHE_TTL) {
      return installStatusCache.status;
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/install/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      // API returned an error status - server is reachable but something is wrong
      return 'unreachable';
    }

    const result = await response.json();

    // Backend wraps responses in { success, data, timestamp }
    let data;
    if (result && typeof result === 'object' && 'success' in result && 'data' in result) {
      data = result.data;
    } else {
      data = result;
    }

    const installed = Boolean(data?.installed);

    const status: InstallStatus = installed ? 'installed' : 'not_installed';

    // Update cache
    installStatusCache = {
      status,
      timestamp: Date.now(),
    };

    return status;
  } catch (error) {
    // Network error - API is unreachable
    console.error('Failed to check install status:', error);
    return 'unreachable';
  }
}

/**
 * Clear the install status cache
 * Call this after installation is complete
 */
export function clearInstallStatusCache(): void {
  installStatusCache = null;
}
